import { Divider } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';
import { ReactNode, useEffect, useRef } from 'react';
import { Node, useEdgesState, useNodesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { compareByName } from '../../../../../react-commons/utils/utilFunctions';
import { formatEnum } from '../../../resource/enumerations/utils/formatEnum';
import { VersionState } from '../../../resource/enumerations/VersionState';
import { GraphDiagramOutline } from '../../../resource/outlines/graphDiagramOutline';
import {
    maxVariantedVersionGraphNodeHeight,
    variantedVersionGraphNodeWidth,
} from './types/VariantedVersionGraphNodeType';
import { versionGraphNodeHeight, versionGraphNodeWidth } from './types/VersionGraphNodeType';
import { VersionGraphContent } from './VersionGraphContent';

export interface VersionGraphNode {
    id: string;
    position: { x: number; y: number };
    data: {
        id: string;
        label: string;
        versionNumber: string;
        versionState: string;
        variants?: Array<{ id: string; name: string }> | null;
        hasParent: boolean;
        hasChild: boolean;
        isActive: boolean;
    };
    type?: string;
}

export interface VersionGraphEdge {
    id: string;
    source: string;
    target: string;
}

export enum VersionNodeVariantStatus {
    ADDED = 'ADDED',
    CHANGED = 'CHANGED',
    UNCHANGED = 'UNCHANGED',
}

export const VersionGraph = ({
    graphVersionDiagramQuery,
    activeId,
    getNodeContextMenuItems,
    backgroundActions,
    onNodeClick,
    withVariants,
}: {
    graphVersionDiagramQuery: UseQueryResult<
        | Array<{
              versionId: string;
              versionInfo: { precedingVersion: string | null; versionState: VersionState; versionNumber: string };
              description: string | null;
              newerVersions: GraphDiagramOutline[];
              isPublished: boolean;
          }>
        | undefined
    >;
    activeId: string | undefined;
    getNodeContextMenuItems?: (node: Node) => { onClick: () => void; title: string; key: string; disabled?: boolean }[];
    backgroundActions?: ReactNode | ReactNode[];
    onNodeClick?: (nodeId: string, nodeLabel: string) => any;
    withVariants?: boolean;
}) => {
    const xOffset = withVariants ? variantedVersionGraphNodeWidth + 100 : versionGraphNodeWidth + 50;
    const yOffset = withVariants ? -(maxVariantedVersionGraphNodeHeight + 100) : -(versionGraphNodeHeight + 50);
    const backgroundActionsHeight = 43;

    let xGlobal = 0;

    const [initialNodes, setInitialNodes] = useNodesState([]);
    const [initialEdges, setInitialEdges] = useEdgesState([]);

    const data = graphVersionDiagramQuery.data ?? [];

    const parseTree = () => {
        const nodes: VersionGraphNode[] = [];
        const edges: VersionGraphEdge[] = [];
        if (data.length > 0) {
            xGlobal = 0;
            const sortedRootNodes = data
                .map((rootNode) => {
                    return {
                        ...rootNode,
                        name: rootNode.versionInfo.versionNumber,
                    };
                })
                .toSorted(compareByName);
            const yStart = (getDeepestLevel() - 1) * -yOffset;
            sortedRootNodes.map((graphVersionDiagram, index) => {
                if (index !== 0) {
                    xGlobal = xGlobal + xOffset;
                }
                const hasChild = graphVersionDiagram.newerVersions.length > 0;
                const rootNode: VersionGraphNode = {
                    id: graphVersionDiagram.versionId,
                    position: { x: xGlobal, y: yStart },
                    data: {
                        id: graphVersionDiagram.versionId,
                        label: graphVersionDiagram.description ?? '',
                        versionNumber: graphVersionDiagram.versionInfo.versionNumber,
                        versionState:
                            graphVersionDiagram.isPublished ?
                                `${formatEnum(VersionState, graphVersionDiagram.versionInfo.versionState)} (Published)`
                            :   formatEnum(VersionState, graphVersionDiagram.versionInfo.versionState),
                        hasParent: false,
                        hasChild,
                        isActive: graphVersionDiagram.versionId === activeId,
                    },
                    type: withVariants ? 'variantedVersionGraphNode' : 'versionGraphNodeType',
                };
                nodes.push(rootNode);
                parseTreeRecursive(graphVersionDiagram, xGlobal, yStart, nodes, edges);
            });
        }
        return { initialNodes: nodes, initialEdges: edges };
    };

    const getDeepestLevel = () => {
        let deepestLevel = 0;
        data.forEach((value) => {
            const nodesDeepestLevels = getNumberOfLevelsRecursive(value);
            if (nodesDeepestLevels > deepestLevel) {
                deepestLevel = nodesDeepestLevels;
            }
        });
        return deepestLevel;
    };

    const getNumberOfLevelsRecursive = (node: GraphDiagramOutline) => {
        let deepestChild = 0;
        node.newerVersions?.forEach((child) => {
            const childLevels = getNumberOfLevelsRecursive(child);
            if (childLevels > deepestChild) {
                deepestChild = childLevels;
            }
        });
        return deepestChild + 1;
    };

    const parseTreeRecursive = (
        node: GraphDiagramOutline,
        x: number,
        y: number,
        finalNodes: VersionGraphNode[],
        finalEdges: VersionGraphEdge[]
    ) => {
        node.newerVersions?.map((child, index) => {
            const yChild = y + yOffset;
            let xChild = x;
            if (index !== 0) {
                xGlobal = xGlobal + xOffset;
                xChild = xGlobal;
            }
            parseTreeRecursive(child, xChild, yChild, finalNodes, finalEdges);
            const hasChild = child.newerVersions !== undefined && child.newerVersions.length > 0;
            const newNode: VersionGraphNode = {
                id: child.versionId,
                position: { x: xChild, y: yChild },
                data: {
                    id: child.versionId,
                    label: child.description ?? '',
                    versionNumber: child.versionInfo.versionNumber,
                    versionState:
                        child.isPublished ?
                            `${formatEnum(VersionState, child.versionInfo.versionState)} (Published)`
                        :   formatEnum(VersionState, child.versionInfo.versionState),
                    hasParent: true,
                    hasChild,
                    isActive: child.versionId === activeId,
                },
                type: withVariants ? 'variantedVersionGraphNode' : 'versionGraphNodeType',
            };
            finalNodes.push(newNode);
            const newEdge = {
                id: child.versionId,
                source: node.versionId,
                target: child.versionId,
                type: 'version-graph-edge',
            };
            finalEdges.push(newEdge);
        });
    };

    useEffect(() => {
        const treeElements = parseTree();
        setInitialNodes(treeElements.initialNodes);
        setInitialEdges(treeElements.initialEdges);
    }, [activeId, withVariants, graphVersionDiagramQuery.data]);

    const divRef = useRef<HTMLDivElement>(null);

    return (
        <>
            {backgroundActions && (
                <div style={{ height: backgroundActionsHeight, width: '100%' }}>
                    <div
                        style={{
                            height: backgroundActionsHeight - 5,
                            maxHeight: backgroundActionsHeight - 5,
                            width: '100%',
                            display: 'inline-flex',
                        }}
                    >
                        <div style={{ flexGrow: 1 }} />
                        {backgroundActions}
                    </div>
                    <Divider />
                </div>
            )}
            {initialNodes.length > 0 && (
                <div
                    ref={divRef}
                    style={{
                        width: '100%',
                        height: backgroundActions ? `calc(100% - ${backgroundActionsHeight}px)` : '100%',
                    }}
                >
                    <VersionGraphContent
                        initialNodes={initialNodes}
                        initialEdges={initialEdges}
                        onNodeClick={onNodeClick}
                        getNodeContextMenuItems={getNodeContextMenuItems}
                        divRef={divRef}
                    />
                </div>
            )}
        </>
    );
};
